<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { SupportedCurrency } from '@/types/booking-api';

import { convertOctoPrice, formatPrice } from '@/utils/utils';

type Unit = {
  id: string;
  description: string;
  quantity: number;
  originalPricePerUnit: number;
  netPricePerUnit: number;
};

const props = withDefaults(defineProps<{
  units: Unit[];
  currency?: SupportedCurrency;
  currencyPrecision?: number;
  loading?: boolean;
}>(), {
  currency: 'EUR',
  currencyPrecision: 2,
});

const { t } = useI18n();

const totalRetail = computed(() => props.units.reduce((acc, u) => acc + u.netPricePerUnit * u.quantity, 0));
const totalOriginal = computed(() => props.units.reduce((acc, u) => acc + u.originalPricePerUnit * u.quantity, 0));

const discountPercentage = computed(() => {
  const finalPrice = totalRetail.value;
  const original = totalOriginal.value;
  if (!original || !finalPrice || original <= finalPrice) {
    return 0;
  }
  return Math.round(((original - finalPrice) / original) * 100);
});
</script>

<template>
  <div class="pricing-breakdown" :aria-busy="loading ? 'true' : undefined">
    <h4 class="pricing-title">
      {{ t('booking.pricingBreakdown') }}
    </h4>

    <template v-if="loading">
      <div class="skeleton-lines" aria-hidden="true">
        <div class="skeleton-line">
          <span class="yb-skeleton skeleton-label" />
          <span class="yb-skeleton skeleton-value" />
        </div>
        <div class="skeleton-line">
          <span class="yb-skeleton skeleton-label-short" />
          <span class="yb-skeleton skeleton-value" />
        </div>
      </div>
      <div class="pricing-separator" aria-hidden="true" />
      <div class="skeleton-total" aria-hidden="true">
        <span class="yb-skeleton skeleton-total-label" />
        <span class="yb-skeleton skeleton-total-value" />
      </div>
    </template>

    <template v-else-if="units.length > 0">
      <div class="pricing-lines">
        <template v-for="unit in units" :key="unit.id">
          <div class="pricing-line">
            <span class="pricing-label">
              {{ formatPrice(convertOctoPrice(unit.originalPricePerUnit, currencyPrecision), currency, true) }}
              × {{ unit.quantity }} {{ unit.description.toLowerCase() }}
            </span>
            <span class="pricing-value">
              {{ formatPrice(convertOctoPrice(unit.originalPricePerUnit * unit.quantity, currencyPrecision), currency, true) }}
            </span>
          </div>
          <div v-if="unit.originalPricePerUnit > unit.netPricePerUnit" class="pricing-line discount-line">
            <span class="pricing-label discount-label">
              {{ t('booking.discountApplied') }}
            </span>
            <span class="pricing-value discount-value">
              -{{ formatPrice(convertOctoPrice((unit.originalPricePerUnit - unit.netPricePerUnit) * unit.quantity, currencyPrecision), currency, true) }}
            </span>
          </div>
        </template>
      </div>

      <div class="pricing-separator" />

      <div class="total-container">
        <span class="pricing-label total-label">{{ t('booking.total') }}</span>
        <div class="total-prices">
          <div>
            <span v-if="totalOriginal > totalRetail" class="original-total">
              {{ formatPrice(convertOctoPrice(totalOriginal, currencyPrecision), currency, true) }}
            </span>
            <span class="final-price">
              {{ formatPrice(convertOctoPrice(totalRetail, currencyPrecision), currency, true) }}
            </span>
          </div>
          <p v-if="discountPercentage !== 0" class="discount-percentage">
            {{ t('booking.discountLabel') }}
            <span>{{ discountPercentage }}%</span>
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="css">
.pricing-title {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  margin: 0 0 var(--yb-spacing-xs) 0;
  color: var(--yb-color-black);
}

.pricing-lines {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-xs);
}

.pricing-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--yb-text-sm);
  color: var(--yb-color-black);
}

.pricing-label {
  font-weight: var(--yb-text-light);
}

.pricing-value {
  font-weight: var(--yb-text-semi-bold);
}

.pricing-separator {
  width: 100%;
  height: 1px;
  background-color: var(--yb-color-mediumgray);
  margin: var(--yb-spacing-xs) 0;
}

.total-container {
  display: flex;
  justify-content: space-between;
}

.total-label {
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-black);
}

.final-price {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-black);
}

.discount-value {
  color: var(--yb-color-success);
  font-weight: var(--yb-text-semi-bold);
}

.discount-line {
  margin-bottom: var(--yb-spacing-sm);
}

.total-prices {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.total-prices div {
  display: flex;
  flex-direction: row;
  align-items: center;

  span {
    margin-left: var(--yb-spacing-xs);
  }
}

.discount-percentage {
  color: var(--yb-color-success);
  font-weight: var(--yb-text-semi-bold);
  font-size: var(--yb-text-xs);

  span {
    font-weight: var(--yb-text-bold);
  }
}

.original-total {
  text-decoration: line-through;
  font-weight: var(--yb-text-light);
  font-size: var(--yb-text-sm);
  color: var(--yb-color-darkgray);
}

.skeleton-lines {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-xs);
}

.skeleton-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.skeleton-label {
  width: 60%;
  height: 16px;
}

.skeleton-label-short {
  width: 40%;
  height: 16px;
}

.skeleton-value {
  width: 60px;
  height: 16px;
}

.skeleton-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.skeleton-total-label {
  width: 50px;
  height: 20px;
}

.skeleton-total-value {
  width: 80px;
  height: 20px;
}
.pricing-breakdown {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
  padding: 0;
  border-radius: 8px;
  margin-top: var(--yb-spacing-xs);
}
</style>
